import axios, {
  AxiosError,
  type AxiosAdapter,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { getMockResponse } from "./mockHandlers.ts";
import { resolvePayablesBinary } from "./payablesMock.ts";

let installed = false;

// Monkey-patches axios.create so every instance created afterwards (including
// the ones cached in react-api-core's useAPI) routes requests through a local
// adapter that returns demo fixtures instead of hitting the backend.
export function installDemoMockAxios() {
  if (installed) return;
  installed = true;
  // eslint-disable-next-line no-console
  console.info("[demo] axios mock adapter installed");

  const originalCreate = axios.create.bind(axios);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (axios as any).create = function patchedCreate(config?: unknown) {
    const instance = originalCreate(config as never);
    instance.defaults.adapter = demoAdapter;
    return instance;
  };

  // Cover any code paths that use the default axios export directly.
  axios.defaults.adapter = demoAdapter;
}

const demoAdapter: AxiosAdapter = async (config: InternalAxiosRequestConfig) => {
  const fullUrl = buildFullUrl(config);
  const body = extractBody(config);
  const method = (config.method ?? "get").toUpperCase();

  // eslint-disable-next-line no-console
  console.debug("[demo] mock", method, fullUrl);
  await sleep(simulateLatencyMs(fullUrl));

  const data = getMockResponse(fullUrl, body, method);

  // Binary path — invoice attachment / ISDOC downloads come back as a
  // `__payablesBinary` marker that resolves to the uploaded file, a generated
  // PDF or the exported ISDOC XML.
  const binary = await resolvePayablesBinary(data);
  if (binary) {
    return {
      data: binary.blob,
      status: 200,
      statusText: "OK",
      headers: { "content-type": binary.contentType },
      config,
      request: {},
    };
  }

  // Error path — handlers return `{ __mockError: { status, data } }` to
  // reproduce a backend rejection (e.g. invoice precondition errors).
  const mockError = (data as { __mockError?: { status: number; data: unknown } } | null)
    ?.__mockError;
  if (mockError) {
    const errorResponse: AxiosResponse = {
      data: mockError.data,
      status: mockError.status,
      statusText: "Bad Request",
      headers: { "content-type": "application/json" },
      config,
      request: {},
    };
    throw new AxiosError(
      `Request failed with status code ${mockError.status}`,
      AxiosError.ERR_BAD_REQUEST,
      config,
      {},
      errorResponse,
    );
  }

  const response: AxiosResponse = {
    data,
    status: 200,
    statusText: "OK",
    headers: { "content-type": "application/json" },
    config,
    request: {},
  };
  return response;
};

function buildFullUrl(config: InternalAxiosRequestConfig): string {
  const base = config.baseURL ?? "";
  const url = config.url ?? "";
  if (/^https?:\/\//i.test(url)) return url;
  if (!base) return url;
  return `${base.replace(/\/$/, "")}/${url.replace(/^\//, "")}`;
}

function extractBody(config: InternalAxiosRequestConfig): Record<string, unknown> {
  const params = paramsToObject(config.params);
  // Multipart-ish uploads (invoice attachment) send the File as the body and
  // the request JSON in `?request=…`.
  if (typeof Blob !== "undefined" && config.data instanceof Blob) {
    return { ...params, __file: config.data };
  }
  if (!config.data) return { ...params };
  if (typeof config.data === "string") {
    try {
      return { ...params, ...(JSON.parse(config.data) as Record<string, unknown>) };
    } catch {
      return { ...params };
    }
  }
  if (typeof config.data === "object") {
    return { ...params, ...(config.data as Record<string, unknown>) };
  }
  return { ...params };
}

function simulateLatencyMs(url: string): number {
  if (/getDocumentList|readEntities/.test(url)) return 180;
  return 90;
}

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function paramsToObject(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  const entries =
    raw instanceof URLSearchParams
      ? Object.fromEntries(raw.entries())
      : (raw as Record<string, unknown>);
  const request = entries.request;
  if (typeof request === "string") {
    try {
      return { ...entries, ...(JSON.parse(request) as Record<string, unknown>) };
    } catch {
      return { ...entries };
    }
  }
  return { ...entries };
}
